import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import RestoreOutlinedIcon from '@mui/icons-material/RestoreOutlined';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import ManageAccountsOutlinedIcon from '@mui/icons-material/ManageAccountsOutlined';
import { useNavigate } from 'react-router-dom';
import {
  archivePlatformTenant,
  clearSelectedPlatformTenant,
  getSelectedPlatformTenant,
  listPlatformTenants,
  mapLaravelError,
  restorePlatformTenant,
  selectPlatformTenant,
} from '../../services/platformTenantsApi';
import {
  formatLicenseSummary,
  formatLicenseWindow,
  licenseStatusColor,
  licenseStatusLabel,
} from '../../utils/tenantLicense';

const TENANT_BASE_PATH = '/super-admin/platform/tenants';

const statusColor = (status) => {
  const value = String(status || '').toLowerCase();
  if (value === 'active') return 'success';
  if (value === 'suspended') return 'warning';
  if (value === 'archived') return 'default';
  return 'default';
};

export default function TenantListPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState('');
  const [selectedTenantKey, setSelectedTenantKey] = useState(() => getSelectedPlatformTenant()?.key || '');

  const loadTenants = useCallback(() => {
    let active = true;

    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const payload = await listPlatformTenants();
        if (!active) return;
        setRows(Array.isArray(payload) ? payload : []);
      } catch (requestError) {
        if (!active) return;
        setError(mapLaravelError(requestError).message);
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => loadTenants(), [loadTenants]);

  const filteredRows = useMemo(() => {
    const q = String(query || '').trim().toLowerCase();
    if (!q) return rows;

    return rows.filter((tenant) => {
      const haystack = [
        tenant?.display_name,
        tenant?.short_name,
        tenant?.product_label,
        tenant?.key,
        tenant?.org_code,
        tenant?.status,
        tenant?.database_connection?.database,
        ...(Array.isArray(tenant?.domains) ? tenant.domains.map((item) => item.domain) : []),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return haystack.includes(q);
    });
  }, [query, rows]);

  const handleSelectTenant = (tenant) => {
    selectPlatformTenant(tenant);
    setSelectedTenantKey(tenant.key);
  };

  const handleManageUsers = (tenant) => {
    handleSelectTenant(tenant);
    navigate('/admin/users');
  };

  const replaceTenantRow = (tenant) => {
    setRows((previous) => previous.map((row) => (
      String(row.id) === String(tenant.id) ? tenant : row
    )));
  };

  const handleArchiveTenant = async (tenant) => {
    if (!window.confirm(`Archive ${tenant.display_name}?`)) {
      return;
    }

    setError('');
    try {
      const payload = await archivePlatformTenant(tenant.id);
      if (payload?.tenant) {
        replaceTenantRow(payload.tenant);
      }
      if (selectedTenantKey === tenant.key) {
        clearSelectedPlatformTenant();
        setSelectedTenantKey('');
      }
    } catch (requestError) {
      setError(mapLaravelError(requestError).message);
    }
  };

  const handleRestoreTenant = async (tenant) => {
    setError('');
    try {
      const payload = await restorePlatformTenant(tenant.id);
      if (payload?.tenant) {
        replaceTenantRow(payload.tenant);
      }
    } catch (requestError) {
      setError(mapLaravelError(requestError).message);
    }
  };

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      <Stack spacing={2}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }}>
          <Stack spacing={0.5}>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Tenant Management
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Super admin workspace for tenant identity, domains, dated licenses, and isolated database connections.
            </Typography>
          </Stack>

          <Button
            variant="contained"
            startIcon={<AddOutlinedIcon />}
            onClick={() => navigate(`${TENANT_BASE_PATH}/new`)}
          >
            Create Tenant
          </Button>
        </Stack>

        {error ? <Alert severity="error">{error}</Alert> : null}

        <Paper variant="outlined" sx={{ p: 2 }}>
          <TextField
            fullWidth
            size="small"
            label="Search tenants"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search by name, org code, key, domain, or database"
          />
        </Paper>

        <Paper variant="outlined">
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Tenant</TableCell>
                  <TableCell>Org Code</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Users</TableCell>
                  <TableCell>License</TableCell>
                  <TableCell>Primary Domain</TableCell>
                  <TableCell>Database</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!loading && filteredRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <Typography variant="body2" color="text.secondary">
                        No tenants found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : null}

                {filteredRows.map((tenant) => {
                  const primaryDomain = Array.isArray(tenant?.domains)
                    ? tenant.domains.find((item) => item.is_primary)?.domain || tenant.domains[0]?.domain
                    : '';
                  const metrics = tenant?.metrics || {};
                  const users = metrics?.users || {};
                  const licenseUsage = tenant?.license_usage || {};
                  const licenseStatus = tenant?.license_status || '';
                  const currentLicense = tenant?.current_license || null;
                  const isSelected = selectedTenantKey === tenant.key;

                  return (
                    <TableRow key={tenant.id} hover selected={isSelected}>
                      <TableCell>
                        <Stack spacing={0.25}>
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              {tenant.display_name}
                            </Typography>
                            {isSelected ? <Chip size="small" color="primary" label="Selected" /> : null}
                          </Stack>
                          <Typography variant="caption" color="text.secondary">
                            key: {tenant.key}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>{tenant.org_code}</TableCell>
                      <TableCell>
                        <Chip size="small" color={statusColor(tenant.status)} label={tenant.status || 'unknown'} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {users.active ?? 0} active / {users.total ?? 0} total
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {formatLicenseSummary(licenseUsage)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                          {currentLicense ? formatLicenseWindow(currentLicense) : licenseStatusLabel(licenseStatus)}
                        </Typography>
                        <Chip sx={{ mt: 0.75 }} size="small" color={licenseStatusColor(licenseStatus)} label={licenseStatusLabel(licenseStatus)} />
                      </TableCell>
                      <TableCell>{primaryDomain || '-'}</TableCell>
                      <TableCell>{tenant?.database_connection?.database || '-'}</TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Button
                            size="small"
                            variant={isSelected ? 'contained' : 'outlined'}
                            startIcon={<CheckCircleOutlineOutlinedIcon />}
                            onClick={() => handleSelectTenant(tenant)}
                          >
                            Select
                          </Button>
                          <Button
                            size="small"
                            startIcon={<ManageAccountsOutlinedIcon />}
                            onClick={() => handleManageUsers(tenant)}
                          >
                            Manage Users
                          </Button>
                          <Button
                            size="small"
                            startIcon={<VisibilityOutlinedIcon />}
                            onClick={() => navigate(`${TENANT_BASE_PATH}/${tenant.id}`)}
                          >
                            View
                          </Button>
                          <Button
                            size="small"
                            startIcon={<EditOutlinedIcon />}
                            onClick={() => navigate(`${TENANT_BASE_PATH}/${tenant.id}/edit`)}
                          >
                            Edit
                          </Button>
                          {String(tenant.status).toLowerCase() === 'archived' ? (
                            <Button
                              size="small"
                              startIcon={<RestoreOutlinedIcon />}
                              onClick={() => handleRestoreTenant(tenant)}
                            >
                              Restore
                            </Button>
                          ) : (
                            <Button
                              size="small"
                              color="error"
                              startIcon={<ArchiveOutlinedIcon />}
                              onClick={() => handleArchiveTenant(tenant)}
                            >
                              Archive
                            </Button>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Stack>
    </Box>
  );
}
